import { Component, Input, OnInit } from '@angular/core'
import { Observable, of, from } from 'rxjs'
import { map, concatMap, reduce } from 'rxjs/operators'

import { BlockFieldsService } from 'src/app/block-services/block-fields.service'
import { BlockDataService } from 'src/app/block-services/block-data.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { MixpanelService } from 'src/app/tracking/mixpanel.service'

import { ProductsService } from 'src/app/e-commerce/products/products.service'
import { CategoriesService } from 'src/app/e-commerce/categories/categories.service'
import { SettingsService } from 'src/app/e-commerce/settings/settings.service'

import { VendorService, Vendor } from '../../multivendor/vendor.service'
import { HelpersService } from 'src/app/helpers.service'

@Component({
  selector: 'vendor-products',
  templateUrl: './vendor-products.component.html',
  styleUrls: ['./vendor-products.component.scss']
})
export class VendorProductsComponent implements OnInit {
  // UI state
  previewBlock = false
  styleFields = false
  previewFields = false

  // inputs
  @Input() importedBlockData: any
  @Input() nestedBlock: any = {}

  // block data
  blockData: any
  blockId = ''
  blockProducts = ''

  // data bags
  productsData: any[] = []
  shownProducts: any[] = []
  allCategories: any[] = []
  allowedSubcategories: any[] = []
  initialData: any = false
  showCategoriesList = false

  // vendors
  vendors: Vendor[] = []
  selectedVendorId: string = 'ALL'

  // caches
  private vendorProductsCache: Record<string, any[]> = {}
  private aggregatedProductsCache: any[] | null = null

  constructor(
    public blockDataService: BlockDataService,
    private blockFieldsService: BlockFieldsService,
    private productsService: ProductsService,
    public builderService: BuilderService,
    private stacksCategoriesService: CategoriesService,
    private mixPanel: MixpanelService,
    public settingsService: SettingsService,
    public helpers: HelpersService,
    private vendorService: VendorService
  ) { }

  ngOnInit(): void {
    // If we are rendering a saved block (page reload / imported view)
    if (this.importedBlockData) {
      this.blockData = this.importedBlockData;
      this.blockData.type = 'multivendorProducts'; // ensure correct type on reload
      this.previewBlock = true;
      this.previewFields = false;
      this.styleFields = false;
    } else if (this.initialData !== 'initial') {
      // When added programmatically as a nested block
      if (this.nestedBlock?.type === 'multivendorProducts') {
        this.blockData = this.nestedBlock;
        this.previewBlock = true;
      } else {
        // fallback to factory/default fields
        this.blockData = this.blockFieldsService.getVendorProductsBlockFields();
        this.blockData.type = 'multivendorProducts'; // ensure correct type for saving
      }
    } else {
      this.blockData = this.blockDataService.getSelectedBlockData(this.blockId);
    }

this.blockData.id = this.blockId;

    // load vendors → then load products
    this.vendorService.vendors.subscribe((list) => {
      this.vendors = this.vendorService.getActiveVendors()
      this.syncSelectedVendorIdForBadge()
      this.builderService.showSettingsProgress = true
      this.loadProductsForCurrentContext().subscribe(() => {
        this.loadCategories()
        this.builderService.showSettingsProgress = false
      })
    })
    this.vendorService.refresh()
  }

  // ---------- UI plumbing ----------
  showData(blockId: any) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      blockId = this.blockDataService.getLatestBlockId()
    }
    this.blockFieldsService.clickedBlockId = blockId
    this.blockDataService.sendCurrentBlock(VendorProductsComponent)
  }

  showStyleTab = () => {
    this.previewBlock = false
    this.previewFields = false
    this.styleFields = true
  }

  showDataTab = () => {
    this.previewBlock = false
    this.previewFields = true
    this.styleFields = false
  }

  checkSidebar = () => !this.previewBlock && !this.previewFields && !this.styleFields
  checkPreview = () => this.previewBlock && !this.previewFields && !this.styleFields
  checkData = () => this.previewFields
  checkStyle = () => this.styleFields && !this.previewFields && !this.previewBlock
  checkSettings = () => !this.checkSidebar() && !this.checkPreview()

  deleteBlock = (blockId: any) => this.blockDataService.removeBlockData(blockId)

  addBlockToPreview(initial: any = '') {
    let blockProductsData = {
      component: VendorProductsComponent,
      index: 'latest',
      element: this,
      initial: initial
    }
    this.blockDataService.sendBlockData(blockProductsData)
    this.builderService.blocksCollapsed = true
  }

  appendBlock($event: any) {
    if (!this.builderService.standalone) return false
    const cloned = { ...this.blockData, type: 'multivendorProducts' }
    this.blockId = this.blockDataService.appendBlocksData(cloned)
    this.addBlockToPreview()
    this.builderService
      .logData('Vendor Products Block Added', 'Block Add', 'Vendor Products Block Added')
      .subscribe(() => { }, () => { })
    this.mixPanel.track('Block Added', {
      projectid: this.builderService.getSelectedProject(),
      block: 'vendorProducts'
    })
    return true
  }

  // ---------- Style tab handler (this was missing) ----------
  styleChange(style: any, blockId: any, $event: any, type: string, stylePosition: 'style' | 'buttonClass' = 'style') {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)

    let data = ''
    if (type === 'select') {
      data = $event.value
    } else if (type === 'text') {
      data = $event.currentTarget.value
    } else if (type === 'slider') {
      data = $event.currentTarget.value + 'px'
    }

    if (stylePosition === 'buttonClass') {
      const cloned = { ...(fields.buttonClass || {}) }
      cloned[style] = data
      fields.buttonClass = cloned
    } else {
      const cloned = { ...(fields.style || {}) }
      cloned[style] = data
      fields.style = cloned
    }

    // re-apply filters so changes like columns/layout reflect immediately
    this.adjustArgs(blockId, fields.data || {})
  }

  // ---------- Data tab (queries) ----------
  queryChange(query: any, blockId: any, $event: any, type: string) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    const cloned = { ...(fields.data || {}) }

    let data = ''
    if (type === 'select' || type === 'slider') data = $event.value ?? $event.currentTarget?.value
    if (type === 'text') data = $event.currentTarget.value

    cloned[query] = data
    fields.data = cloned

    this.showCategoriesList = fields.data.query_post_type === 'by_category'

    if (query === 'source' || query === 'vendors') {
      this.aggregatedProductsCache = null
      this.syncSelectedVendorIdForBadge()
      this.builderService.showSettingsProgress = true
      this.loadProductsForCurrentContext(fields.data).subscribe(() => {
        this.adjustArgs(blockId, fields.data)
        this.loadCategories()
        this.builderService.showSettingsProgress = false
      })
      return
    }

    this.adjustArgs(blockId, cloned)
  }

  // ---------- product loading (vendor-aware) ----------
  private loadProductsForCurrentContext(dataWithVendorId? : any): Observable<void> {
    const args = dataWithVendorId ? dataWithVendorId : this.blockData?.data
    const source: 'all' | 'by_id' = (args.source || 'all') as any
    const selected: string[] = Array.isArray(args.vendors) ? args.vendors : []

    const finalize = (arr: any[]) => {
      const normalized = this.sanitizeProductsForTemplate(arr || [])
      this.shownProducts = normalized
      this.decoratePrices(this.shownProducts)
      this.adjustArgs(this.blockId, this.blockData.data || {})
    }

    if (source === 'all') {
      if (this.aggregatedProductsCache) {
        finalize(this.aggregatedProductsCache.slice())
        return of(void 0)
      }

      if (!this.vendors.length) {
        // fallback to current project
        return this.getProducts({}).pipe(
          map((resp) => {
            const products = Array.isArray(resp) ? resp : Object.values(resp || {})
            finalize(products)
          }),
          map(() => void 0)
        )
      }

      // aggregate all vendors sequentially
      this.builderService.showSettingsProgress = true
      return from(this.vendors).pipe(
        concatMap((v) => this.getProductsForVendor(v.id).pipe(map((arr) => this.attachVendorMeta(arr, v)))),
        reduce((acc, list) => acc.concat(list), [] as any[]),
        map((merged) => {
          this.aggregatedProductsCache = merged
          finalize(merged.slice())
          this.builderService.showSettingsProgress = false
        }),
        map(() => void 0)
      )
    }

    // by_id
    if (!selected.length) {
      finalize([])
      return of(void 0)
    }

    if (selected.length === 1) {
      const id = selected[0]
      return this.getProductsForVendor(id).pipe(
        map((products) => {
          const v = this.vendors.find((x) => x.id === id) || null
          finalize(this.attachVendorMeta(products, v))
        }),
        map(() => void 0)
      )
    }

    // multiple vendors
    this.builderService.showSettingsProgress = true
    return from(selected).pipe(
      concatMap((id) => {
        const v = this.vendors.find((x) => x.id === id) || null
        return this.getProductsForVendor(id).pipe(map((arr) => this.attachVendorMeta(arr, v)))
      }),
      reduce((acc, list) => acc.concat(list), [] as any[]),
      map((merged) => {
        finalize(merged)
        this.builderService.showSettingsProgress = false
      }),
      map(() => void 0)
    )
  }

  private getProductsForVendor(vendorId: string): Observable<any[]> {
    if (this.vendorProductsCache[vendorId]) {
      return of(this.vendorProductsCache[vendorId])
    }
    ; (this.productsService as any).storedVendorProducts = null
    this.builderService.showSettingsProgress = true
    return this.productsService.getVendorProducts(vendorId).pipe(
      map((arr: any[]) => {
        this.vendorProductsCache[vendorId] = arr || []
        this.builderService.showSettingsProgress = false
        return this.vendorProductsCache[vendorId]
      })
    )
  }

  private attachVendorMeta(products: any[], vendor: Vendor | null): any[] {
    if (!products || !products.length) return []
    const id = (vendor?.id ?? 'ALL').toString()
    const name = (vendor?.name || vendor?.id || 'ALL').toString()
    return products.map((p) => {
      const vId = (p?._vendorId ?? id).toString()
      const vendorClass = `vendor-${vId}`
      return { ...p, _vendorId: vId, _vendorName: name, _vendorClass: vendorClass }
    })
  }

  private sanitizeProductsForTemplate(products: any[]): any[] {
    return (products || []).map((p) => ({
      ...p,
      _vendorId: (p?._vendorId ?? this.selectedVendorId ?? 'ALL').toString(),
      _vendorName: (p?._vendorName ?? '').toString(),
      _vendorClass: (`vendor-${p?._vendorId ?? this.selectedVendorId ?? 'ALL'}`).toString(),
      categorySlug: (p?.categorySlug ?? '').toString(),
      badgeClass: (p?.badgeClass ?? '').toString()
    }))
  }

  // ---------- categories ----------
  private loadCategories(): void {
    this.stacksCategoriesService.getCategories().subscribe((resp: any) => {
      this.allCategories = []
      for (const key in resp) {
        if (Object.prototype.hasOwnProperty.call(resp, key)) {
          this.allCategories.push(resp[key])
        }
      }
      this.setCategoriesData(this.blockId, this.allCategories)
    })
  }

  // ---------- parity helpers with stacks-products ----------
  getProducts = (_args: object): Observable<any> => {
    if (this.productsService.allProducts && this.productsService.allProducts.length) {
      return of(this.productsService.allProducts)
    }
    this.builderService.showSettingsProgress = true
    return new Observable<any>((observer) => {
      this.productsService.getProducts().subscribe(
        (response) => {
          this.builderService.showSettingsProgress = false
          observer.next(response)
          observer.complete()
        },
        (err) => {
          this.builderService.showSettingsProgress = false
          observer.error(err)
        }
      )
    })
  }

  private decoratePrices(products: any[]): void {
    products.forEach((product) => {
      product.imageUrl = product.productVariations?.[0].image || this.helpers.getArrayValue(product.imageUrl) || 'assets/images/empty-image.png'
      const prices = this.getLowestPrices(product.productVariations || [])
      product.lowestSalePrice = prices.lowestSalePrice
      product.correspondingRegularPrice = prices.correspondingRegularPrice
      product.lowestRegularPrice = prices.lowestRegularPrice
    })
  }

  getLowestPrices(productVariations: any[]) {
    let lowestSalePrice = Infinity
    let correspondingRegularPrice: number | null = null
    let lowestRegularPrice = Infinity

    productVariations.forEach((variation) => {
      if (!variation?.values) return
      const salePrice = parseFloat(variation.salePrice || '0')
      const regularPrice = parseFloat(variation.regularPrice || '0')

      if (salePrice > 0 && salePrice < lowestSalePrice) {
        lowestSalePrice = salePrice
        correspondingRegularPrice = regularPrice
      }
      if (regularPrice > 0 && regularPrice < lowestRegularPrice) {
        lowestRegularPrice = regularPrice
      }
    })

    return {
      lowestSalePrice: lowestSalePrice === Infinity ? null : lowestSalePrice,
      correspondingRegularPrice: lowestSalePrice === Infinity ? null : correspondingRegularPrice,
      lowestRegularPrice: lowestRegularPrice === Infinity ? null : lowestRegularPrice
    }
  }

  // block data passthroughs
  getProductsData = (blockId: string) => {
    if (this.importedBlockData) return this.importedBlockData.productsData || [];
    if (this.blockDataService.checkBlockAvailability(blockId)) {
      const fields = this.blockDataService.getSelectedBlockData(blockId);
      return fields?.productsData || [];
    }
    // fallback so preview renders before the block is registered
    return this.blockData?.productsData || [];
  };
  setProductsData = (blockId: string, data: any[]) => {
    if (this.blockDataService.checkBlockAvailability(blockId)) {
      const fields = this.blockDataService.getSelectedBlockData(blockId);
      fields.productsData = data;
      // keep local reference in sync and trigger CD (esp. if OnPush up the tree)
      this.blockData = { ...fields };
    } else {
      // fallback for preview/new blocks not yet in the service
      this.blockData = { ...(this.blockData || {}), productsData: data };
    }
  };

  getCategoriesData = (blockId: string) => {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    return fields.categories
  }

  setCategoriesData = (blockId: string, data: object) => {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    fields.categories = data
  }

  adjustArgs = (blockId: any, args: any) => {
    let filteredData: any[] = []

    const limit = args?.limit
    const categories = args?.products_categories
    const sub_categories = args?.products_sub_categories
    const type = args?.query_post_type
    const order_by = args?.query_orderby
    const order_type = args?.query_order

    if (type === 'by_category') {
      this.categoryChange(args.products_categories)
      if (sub_categories && sub_categories.length && this.filterSubCategories(sub_categories).length) {
        filteredData = this.filterSubCategories(sub_categories)
      } else {
        filteredData = this.filterCategories(categories)
      }
    } else {
      filteredData = this.shownProducts || []
    }

    filteredData = this.filterLimit(limit, filteredData)
    filteredData = this.orderProducts(order_by, order_type, filteredData)
    this.setProductsData(blockId, filteredData)
  }

  filterLimit = (limit: any, filteredData: any[]) =>
    filteredData.slice(0, limit || filteredData.length)

  filterCategories = (categories: any) =>
    this.productsService.getProductsByCategories(categories, this.shownProducts || [])

  filterSubCategories = (categories: any) =>
    this.productsService.getProductsBySubCategories(categories, this.shownProducts || [])

  orderProducts = (order_by: string, order_type: string, filteredData: any[]) => {
    if (order_by === 'date') {
      filteredData.sort((a: any, b: any) => {
        const dateA: any = new Date(a.date)
        const dateB: any = new Date(b.date)
        return order_type === 'ASC' ? dateA - dateB : dateB - dateA
      })
    } else if (order_by === 'title') {
      filteredData.sort((a: any, b: any) => {
        const titleA = (a.productName || '').toLowerCase()
        const titleB = (b.productName || '').toLowerCase()
        return order_type === 'ASC' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA)
      })
    } else if (order_by === 'price') {
      filteredData.sort((a: any, b: any) => {
        const priceA: any = parseFloat(a.regularPrice || a.lowestRegularPrice || '0')
        const priceB: any = parseFloat(b.regularPrice || b.lowestRegularPrice || '0')
        return order_type === 'ASC' ? priceA - priceB : priceB - priceA
      })
    }
    return filteredData
  }

  categoryChange = (value: string[]) => {
    const selected = this.stacksCategoriesService.findCategoryById(this.allCategories, value)
    const next: any[] = []
    for (const category of selected) {
      const subs = category?.subcategories
      if (!subs || !subs.length) continue
      for (const s of subs) {
        if (s && typeof s.slug === 'string' && s.slug.trim().length) next.push(s)
      }
    }
    this.allowedSubcategories = next
  }

  private syncSelectedVendorIdForBadge() {
    const source: 'all' | 'by_id' = (this.blockData?.data?.source || 'all') as any
    const selected: string[] = Array.isArray(this.blockData?.data?.vendors) ? this.blockData.data.vendors : []
    this.selectedVendorId = source === 'by_id' && selected.length === 1 ? (selected[0] || 'ALL') : 'ALL'
  }

  // safe helpers for template
  toNgClass(input: any, ...rest: any[]): string[] | { [k: string]: boolean } {
    if (arguments.length > 1) input = [input, ...rest]
    if (!input) return []
    if (Array.isArray(input)) {
      return input.filter((c) => typeof c === 'string').map((c) => c.trim()).filter(Boolean)
    }
    if (typeof input === 'object') {
      const out: Record<string, boolean> = {}
      Object.keys(input || {}).forEach((k) => {
        const kk = (k || '').toString().trim()
        if (kk) out[kk] = !!input[k]
      })
      return out
    }
    if (typeof input === 'string') {
      const s = input.trim()
      return s ? [s] : []
    }
    return []
  }

  getBlockFields = (id: string) => {
    if (this.importedBlockData) return this.importedBlockData;
    if (this.blockDataService.checkBlockAvailability(id)) return this.blockDataService.getSelectedBlockData(id);
    return this.blockData; // fallback so preview can still render
  };
}
